/**
 * More: her profile, the rest of her money screens, the officer tools (on phones), the
 * member/officer view switch (PRD 16: "the role switcher sits in More"), her groups,
 * her signed-in phones, appearance, and signing out.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { BottomSheet, Button, Icon, type IconName } from '../components'
import { formatPhone } from '../lib/phone'
import { officeTitles, useSession } from '../session/useSession'
import { CONSOLE_NAV, OFFICER_TOOLS } from './navigation'
import { PaletteSwitcher, ThemeSwitcher } from './ThemeSwitcher'

function Row({ to, icon, label, hint }: { to: string; icon: IconName; label: string; hint?: string }) {
  return (
    <li>
      <Link to={to} className="more-row">
        <Icon name={icon} />
        <span className="more-row__text">
          <span>{label}</span>
          {hint && <span className="more-row__hint">{hint}</span>}
        </span>
        <Icon name="chevron_right" />
      </Link>
    </li>
  )
}

export function MorePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { me, membership, mode, setMode, setGroup, groupId, signOut, forgetDevice } = useSession()
  const [groupsOpen, setGroupsOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const officer = Boolean(membership?.is_officer)
  const memberships = me?.memberships ?? []

  return (
    <div className="container screen stack more">
      <title>{t('shell.more.metaTitle')}</title>
      <h1 className="t-title">{t('shell.more.title')}</h1>

      <section className="card more-me">
        <Icon name="account_circle" size={40} />
        <div>
          <p className="more-me__name">{me?.person.full_name}</p>
          <p className="t-muted">
            {membership?.label}
            {officeTitles(membership).length ? ` · ${officeTitles(membership).join(', ')}` : ''}
          </p>
          <p className="t-muted tabular">{me ? formatPhone(me.person.phone) : ''}</p>
        </div>
      </section>

      <ul className="more-list">
        <Row to="/app/profile" icon="person" label={t('shell.more.profile')} />
      </ul>

      <section className="stack stack--sm">
        <h2 className="t-heading">{t('shell.more.yourMoney')}</h2>
        <ul className="more-list">
          <Row to="/app/activity" icon="history" label={t('shell.more.activity')} />
          <Row to="/app/pledge" icon="event" label={t('shell.more.pledge')} />
          <Row to="/app/guarantees" icon="handshake" label={t('shell.more.guarantees')} />
          <Row to="/app/statement/group/latest" icon="table_view" label={t('shell.more.groupStatement')} />
        </ul>
      </section>

      {officer && (
        <section className="stack stack--sm more-officer">
          <h2 className="t-heading">{t('shell.more.view')}</h2>
          <p className="t-muted">{t('shell.more.viewHint')}</p>
          <div className="more-view" role="group" aria-label={t('shell.more.view')}>
            <Button variant={mode === 'member' ? 'primary' : 'secondary'} icon="person" aria-pressed={mode === 'member'}
              onClick={() => {
                setMode('member')
                navigate('/app/home')
              }}>
              {t('shell.more.memberView')}
            </Button>
            <Button variant={mode === 'officer' ? 'primary' : 'secondary'} icon="shield"
              aria-pressed={mode === 'officer'} onClick={() => {
                setMode('officer')
                navigate('/app/officer')
              }}>
              {t('shell.more.officerView')}
            </Button>
          </div>
          <h2 className="t-heading more-officer__tools">{t('shell.more.officerTools')}</h2>
          <ul className="more-list">
            {OFFICER_TOOLS.map((item) => (
              <Row key={item.to} to={item.to} icon={item.icon} label={t(`shell.nav.${item.label}`)} />
            ))}
          </ul>
          <h2 className="t-heading">{t('shell.more.console')}</h2>
          <ul className="more-list">
            {CONSOLE_NAV.map((item) => (
              <Row key={item.to} to={item.to} icon={item.icon} label={t(`shell.nav.${item.label}`)}
                hint={t('shell.more.consoleHint')} />
            ))}
          </ul>
        </section>
      )}

      <section className="stack stack--sm">
        <h2 className="t-heading">{t('shell.more.group')}</h2>
        <ul className="more-list">
          {memberships.length > 1 && (
            <li>
              <button type="button" className="more-row" onClick={() => setGroupsOpen(true)} aria-haspopup="dialog">
                <Icon name="swap_horiz" />
                <span className="more-row__text">
                  <span>{t('shell.switchGroup')}</span>
                  <span className="more-row__hint">{membership?.group.name}</span>
                </span>
                <Icon name="chevron_right" />
              </button>
            </li>
          )}
          <Row to="/app/start-group" icon="add" label={t('shell.more.startGroup')} />
        </ul>
      </section>

      <section className="stack stack--sm">
        <h2 className="t-heading">{t('shell.more.security')}</h2>
        <ul className="more-list">
          <Row to="/app/more/devices" icon="mobile" label={t('shell.more.devices')} hint={t('shell.more.devicesHint')} />
        </ul>
      </section>

      <section className="stack stack--sm">
        <h2 className="t-heading">{t('shell.more.app')}</h2>
        <ul className="more-list">
          <Row to="/calculator" icon="calculate" label={t('shell.more.calculator')} />
          <li className="more-row more-row--static">
            <Icon name="language" />
            <span className="more-row__text">
              <span>{t('shell.more.language')}</span>
              <span className="more-row__hint">{t('shell.more.languageValue')}</span>
            </span>
          </li>
        </ul>
        <ThemeSwitcher />
        <PaletteSwitcher />
      </section>

      <section className="stack stack--sm more-signout">
        <Button variant="secondary" icon="logout" loading={busy} onClick={() => {
          setBusy(true)
          void signOut().then(() => navigate('/signin', { replace: true }))
        }}>
          {t('shell.more.signOut')}
        </Button>
        <Button variant="text" onClick={() => {
          void signOut().then(() => {
            forgetDevice()
            navigate('/signin', { replace: true })
          })
        }}>
          {t('shell.more.forget')}
        </Button>
        <p className="t-caption t-muted">{t('shell.more.forgetHint')}</p>
      </section>

      <BottomSheet open={groupsOpen} onClose={() => setGroupsOpen(false)} title={t('shell.switchGroup')}>
        <ul className="group-list">
          {memberships.map((m) => (
            <li key={m.id}>
              <button type="button" className="group-list__item" aria-current={m.group.id === groupId ? 'true' : undefined}
                onClick={() => {
                  setGroup(m.group.id)
                  setGroupsOpen(false)
                  navigate('/app')
                }}>
                <span>
                  <span className="group-list__name">{m.group.name}</span>
                  <span className="group-list__who">{m.label}</span>
                </span>
                {m.group.id === groupId && <Icon name="check" />}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>
    </div>
  )
}
